import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import { History, RotateCcw, ArrowLeft, GitCompare } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticleVersions() {
  const { id } = useParams();
  const { authFetch } = useAdmin();
  const [article, setArticle] = useState(null);
  const [versions, setVersions] = useState([]);
  const [sel, setSel] = useState(null);
  const [diff, setDiff] = useState('');
  const [diffBetween, setDiffBetween] = useState([]); // two selected ids

  const load = useCallback(async () => {
    const [a, v] = await Promise.all([
      authFetch(`${API}/admin/articles/${id}`).then(r => r.json()),
      authFetch(`${API}/admin/articles/${id}/versions`).then(r => r.json()),
    ]);
    setArticle(a); setVersions(v.versions || []);
  }, [authFetch, id]);

  useEffect(() => { load(); }, [load]);

  async function view(vid) {
    const r = await authFetch(`${API}/admin/articles/${id}/versions/${vid}`);
    const d = await r.json();
    setSel(d);
    // diff against current
    const dr = await authFetch(`${API}/admin/articles/${id}/versions-diff?from=${vid}`);
    const dd = await dr.json();
    setDiff(dd.diff || '(nema razlika)');
  }

  async function diffTwo() {
    if (diffBetween.length !== 2) return;
    const [from, to] = diffBetween;
    const r = await authFetch(`${API}/admin/articles/${id}/versions-diff?from=${from}&to=${to}`);
    const d = await r.json();
    setDiff(d.diff || '(nema razlika)');
    setSel(null);
  }

  async function restore(vid) {
    if (!window.confirm('Vratiti ovu verziju? Trenutno stanje bit će spremljeno kao nova verzija.')) return;
    const r = await authFetch(`${API}/admin/articles/${id}/versions/${vid}/restore`, { method: 'POST' });
    if (r.ok) { toast.success('Verzija vraćena'); load(); }
    else toast.error('Greška pri vraćanju');
  }

  function toggleCompare(vid) {
    setDiffBetween(prev => {
      if (prev.includes(vid)) return prev.filter(x => x !== vid);
      if (prev.length >= 2) return [prev[1], vid];
      return [...prev, vid];
    });
  }

  return (
    <div className="p-6 text-zinc-100 max-w-6xl" data-testid="article-versions-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2"><History /> Povijest izmjena</h1>
        <Link to={`/admin/articles/${id}/edit`} className="text-sm text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"><ArrowLeft size={14} /> Natrag na članak</Link>
      </div>
      {article && <div className="text-sm text-zinc-500 mb-4">{article.title_hr}</div>}
      {diffBetween.length === 2 && (
        <button onClick={diffTwo} className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-800 rounded text-sm" data-testid="versions-diff-btn">
          <GitCompare size={14} /> Usporedi odabrane ({diffBetween.length})
        </button>
      )}
      <div className="grid md:grid-cols-[280px,1fr] gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2 max-h-[600px] overflow-auto">
          {versions.length === 0 && <div className="text-zinc-500 p-3 text-sm">Nema verzija.</div>}
          {versions.map(v => (
            <div key={v.id} className={`p-2 rounded mb-1 text-xs ${sel?.id === v.id ? 'bg-zinc-800' : ''}`} data-testid={`version-row-${v.version}`}>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={diffBetween.includes(v.id)} onChange={() => toggleCompare(v.id)} />
                <button onClick={() => view(v.id)} className="flex-1 text-left hover:text-white">
                  <div className="font-medium">v{v.version} · {v.reason}</div>
                  <div className="text-zinc-500">{new Date(v.created_at).toLocaleString('hr-HR')}</div>
                  <div className="text-zinc-600 truncate">{v.actor_email}</div>
                </button>
                <button onClick={() => restore(v.id)} title="Vrati" className="p-1 hover:bg-zinc-700 rounded text-amber-400" data-testid={`version-restore-${v.version}`}>
                  <RotateCcw size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 min-h-[400px]">
          {!diff && !sel && <div className="text-zinc-500 text-sm">Odaberite verziju za pregled.</div>}
          {sel && (
            <div className="mb-4 text-xs text-zinc-500">
              v{sel.version} · {new Date(sel.created_at).toLocaleString('hr-HR')} · {sel.actor_email}
            </div>
          )}
          {diff && (
            <pre className="text-[11px] font-mono bg-zinc-950 border border-zinc-800 rounded p-3 overflow-auto whitespace-pre-wrap" data-testid="versions-diff-output">
{diff.split('\n').map((line, i) => {
  const color = line.startsWith('+') && !line.startsWith('+++') ? 'text-green-400'
    : line.startsWith('-') && !line.startsWith('---') ? 'text-red-400'
    : line.startsWith('@@') ? 'text-blue-400' : 'text-zinc-400';
  return <div key={i} className={color}>{line || ' '}</div>;
})}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
